import { RECORD_STATUS, countries, NRM_SUB_STATUS } from '../../config';

const {
  refused,
  outdated,
  appointment_canceled,
  record_init,
  appointment_planned,
  expertise_restarted,
  expertise_started,
  expertise_authorized,
  auto_accept,
  report,
  repurchase_account,
  expertise_interrupted,
  not_show,
  come_left,
  transaction,
  finish,
  closed,
  justified,
  report_ongoing,
  old_flow,
  waiting_for_counter_appraisal,
  counter_appraisal_ongoing,
  counter_appraisal_verification,
} = RECORD_STATUS;

const {
  tradeInMaintainedPriceMaintained,
  tradeInMaintainedPriceModified,
  finalizationCompleted,
  finalizationAccepted,
  waitingForCounterAppraisal,
  counterAppraisalVerification,
} = NRM_SUB_STATUS;

export class Record {
  constructor(props = {}) {
    this.props = props;
  }

  cancelIsAllowed() {
    return ![record_init, outdated, appointment_planned, appointment_canceled, not_show, come_left].includes(
      this.status(),
    );
  }

  reactivateIsAllowed() {
    return [refused, outdated, appointment_canceled].includes(this.status());
  }

  initializeIsAllowed() {
    return [record_init, appointment_planned].includes(this.status());
  }

  displayBlocAppraisalIsAllowed() {
    return !(
      [record_init, appointment_planned, appointment_canceled, refused].includes(this.status()) || this.type() === 2
    );
  }

  displayBlocsIsAllowed() {
    return ![record_init, appointment_planned, appointment_canceled, refused].includes(this.status());
  }

  displayVehicleIsAllowed() {
    return ![record_init, appointment_planned, refused].includes(this.status());
  }

  editAppointmentIsAllowed() {
    return [record_init, appointment_planned].includes(this.status());
  }

  isHBS() {
    return [1, 11].includes(this.props.record?.originId);
  }

  isNrmSolHbs() {
    return [14].includes(this.props.record?.originId);
  }

  formSource() {
    return this.props.record?.formSource;
  }

  status() {
    return this.props.record?.statusId;
  }

  nrmStatus() {
    return this.props.record?.nrmStatusId;
  }

  maintainedGuaranteedAccepted() {
    return this.props?.guaranteedPrice?.maintainedGuaranteedAccepted;
  }

  isNrmFinalizationActive() {
    return (
      ([tradeInMaintainedPriceModified].includes(this.nrmStatus()) && this.maintainedGuaranteedAccepted()) ||
      [tradeInMaintainedPriceMaintained, waitingForCounterAppraisal].includes(this.nrmStatus())
    );
  }

  isCounterAppraisalFinalizationActive() {
    return this.nrmStatus() === waitingForCounterAppraisal && !this.props.counterInspection?.recordId;
  }

  isNrmFinalizationCompleted() {
    return [finalizationCompleted, finalizationAccepted].includes(this.nrmStatus());
  }

  type() {
    return this.props.record?.recordTypeId;
  }

  CounterAppraisalIsVisibleNrm() {
    return [
      waitingForCounterAppraisal,
      counterAppraisalVerification,
      tradeInMaintainedPriceMaintained,
      tradeInMaintainedPriceModified,
      finalizationCompleted,
      finalizationAccepted,
    ].includes(this.nrmStatus());
  }

  CounterAppraisalIsVisibleB2b(counterInspection) {
    return (
      [waiting_for_counter_appraisal, counter_appraisal_ongoing, counter_appraisal_verification].includes(
        this.status(),
      ) ||
      (!this.isNrmRecord() && counterInspection)
    );
  }

  CounterAppraisalIsVisibleB2bFrevos(counterInspection) {
    return (
      [counter_appraisal_verification].includes(this.status()) ||
      (!this.isNrmRecord() && counterInspection && ![counter_appraisal_ongoing].includes(this.status()))
    );
  }

  nextAction(label) {
    switch (this.status()) {
      case record_init:
        return 'initialize';
      case appointment_planned:
        return 'identification';
      case refused:
      case outdated:
        return 'reactivateRecord';
      case expertise_restarted:
        if (this.type() === 2 && !this.isNrmRecord() && label) {
          return label;
        }
        return 'launchInspection';
      case expertise_started:
        return 'continueInspection';
      default:
        return null;
    }
  }

  finalizationIsVisible() {
    return (
      ([
        expertise_authorized,
        auto_accept,
        report,
        repurchase_account,
        waiting_for_counter_appraisal,
        counter_appraisal_ongoing,
      ].includes(this.status()) &&
        !this.isNrmRecord()) ||
      this.isNrmFinalizationActive() ||
      this.isNrmFinalizationCompleted() ||
      this.isCounterAppraisalFinalizationActive()
    );
  }

  tradeIsVisible() {
    return ![expertise_restarted, expertise_started, refused, expertise_interrupted, outdated].includes(this.status());
  }

  pricingIsVisible() {
    return ![expertise_restarted, expertise_started, refused, expertise_interrupted, outdated].includes(this.status());
  }

  inspectionReport() {
    return ![expertise_restarted, expertise_started, refused, expertise_interrupted, outdated].includes(this.status());
  }

  getAutobizCoverageId() {
    return this.props.record?.autobizCoverageId;
  }

  readOnlyStatus() {
    return [
      auto_accept,
      refused,
      transaction,
      finish,
      closed,
      expertise_interrupted,
      justified,
      report_ongoing,
      old_flow,
      repurchase_account,
      waiting_for_counter_appraisal,
      counter_appraisal_ongoing,
    ].includes(this.status());
  }

  finalisationReadOnlyStatus() {
    return [waiting_for_counter_appraisal, counter_appraisal_ongoing].includes(this.status());
  }

  getSourceId() {
    return this.props.record?.sourceId;
  }

  getGroupId() {
    return this.props.record?.groupId;
  }

  vehicleIdentityBlocIsNotAllowed() {
    return this.status() > 2 && this.status() !== appointment_planned;
  }

  getCountry() {
    return countries[this.props.record?.countryId];
  }

  getRegistration() {
    return this.props.vehicle?.registration;
  }

  isGroupLevel() {
    return this.props.record?.configLevel === 'G';
  }

  editVehicleIdentity() {
    return ![expertise_restarted, expertise_started].includes(this.status());
  }

  getExternalRef() {
    return this.props.vehicle?.externalRef;
  }

  getSourceInfos() {
    return this.props.sourceInfos;
  }

  isNrmRecord() {
    return [10, 11, 12, 13, 14].includes(this.props.record?.originId);
  }

  guaranttedPriceIsActive() {
    return this.status() > 2;
  }

  roleViewerAccessBloc(role, bloc) {
    if (bloc === 'appraisal') return role === 'viewer' && [0, 1, 2].includes(this.status());
    if (bloc === 'counter-appraisal') return role === 'viewer' && this.nrmStatus() === 40;
    return false;
  }

  counterAppraisalEnabled() {
    return this.props.sourceInfos?.sourceGroupData?.enableCounterAppraisal;
  }

  manualDecisionEnabled() {
    return this.props.sourceInfos?.sourceGroupData?.enableManualDecision;
  }

  counterAppraisalRequired() {
    return this.props.supervisor?.counterAppraisalRequired;
  }

  isAutobizMarket() {
    return this.props.record?.originId === 2;
  }
}
